import axios from 'axios'
import { CURRENCY, ENVIRONMENT_URL, SC_SITE } from '@common/constants'
import { MarketoPayload } from '@common/state/marketing/state'
import { ReCAPTCHAValidationResponseData } from '@common/state/reCAPTCHA/state'
import { memoize } from 'lodash-es'
import { BUY_USER_TYPES_RESPONSE } from '@buy/constants'

export interface EmailType {
    emailAddress: string
}

export interface EmailCheckResponse {
    status: number
    data: {
        canUserAuthenticate: boolean
    }
}

/* eslint-disable camelcase */
export interface SiteParamType {
    productFamily: string
    sc_lang?: string
}

// API Reference: https://confluence.ops.expertcity.com/x/qFunCw
export interface FlexTokenDataType {
    targetOrigin: string
    currency: string
    country: string
    skipFlexToken?: boolean
    skipSca?: boolean
}

/* eslint-disable camelcase */
export interface SiteType {
    sc_site: string
}

export function getFlexTokenCall(data: FlexTokenDataType): Promise<any> {
    return axios.post('/ecommapi/purchase/FlexKeyV3', data)
}

export function getExternalLocationInfo() {
    return axios.get(`${ENVIRONMENT_URL}/ecommapi/utility/GetLocationInfo`)
}

export interface LocationInfoResponseData {
    country: string
    currency: CURRENCY // Maybe never used? See src/common/sagas/location/location-info.ts
    currencyRestricitons: CURRENCY[] // No longer used as of UCS CurrencyRestrictions migration. See src/common/sagas/location/location-info.ts
    language: string
}
export function getLocationInfo(): Promise<any> {
    return axios.get('/ecommapi/utility/GetLocationInfo')
}

// get countries
export function getCountries(params?: SiteParamType): Promise<any> {
    return axios.get('/ecommapi/content/countries', { params })
}

export interface VerificationTokenDataType {
    email: string
    theme?: string
}

// marketo
// 11/05/2024 not being used after AWS migration, it will be removed if we are no longer need
export function postMarketo(payload: MarketoPayload, params?: SiteType) {
    return axios.post('/ecommapi/form/SendAsync', payload, {
        withCredentials: false,
        headers: {
            'Content-Type': 'application/json',
        },
        params,
    })
}

// Syncari
export function postSyncari(payload: any) {
    return axios.post(`${process.env.AWS_SYNCARI_ENDPOINT}`, payload, {
        withCredentials: false,
        headers: {
            'Content-Type': 'application/json',
        },
    })
}

// coupon
export function getCouponInfo(couponCode: string) {
    return axios.get(`${ENVIRONMENT_URL}/ecommapi/content/couponinfo/${couponCode}`)
}

// verify user is logged in correctly
export interface ValidateUserResponse {
    Success?: boolean
    Code: BUY_USER_TYPES_RESPONSE | null
    Message: string | null
    User: {
        DisplayName: string
        UserName: string
        UserKey: string
        Name: {
            GivenName: string
            FamilyName: string
        }
    }
    AccountKey: string
    isBanned: boolean
    hasGroupSubscription: boolean
}
export function verifyUser(token: string, productGroup: string) {
    const data = {
        token,
        productGroup,
    }
    return axios.post<ValidateUserResponse>('/ecommapi/identity/ValidateUser', data)
}

export function getBillingSubscriptions() {
    return axios.get('/ecommapi/purchase/BillingDetailedSubscriptions', {
        withCredentials: true,
    })
}

export function getBillingInfo() {
    return axios.get('/ecommapi/purchase/BillingInfo', {
        withCredentials: true,
    })
}

// get existing payment info
export function getExistingPaymentDetails() {
    return axios.post('/ecommapi/purchase/GetStoredPaymentMethods', {
        withCredentials: true,
    })
}

// clear user session
export function clearUserSession(): Promise<any> {
    return axios.post('/ecommapi/identity/ClearSession/')
}

export interface EmailVerificationType {
    accessToken: string | undefined
    emailCode?: string
    emailToken?: string
}

// update user email verification status
export function updateUser(payload: EmailVerificationType) {
    return axios.post('/ecommapi/identity/updateuser', payload)
}

// check if user already has an account
export function canUserAuthenticate(data: EmailType): Promise<any> {
    return axios.post(`${process.env.AUTH_CLIENT_URL}/canUserAuthenticate`, data)
}

// API Reference
// https://confluence.ops.expertcity.com/spaces/SOA/pages/65145556/SCIM+Identity+Service#SCIMIdentityService-UserSchema
export interface GetIdentityUserMeResponseData {
    'urn:scim:schemas:extension:getgo:1.0': {
        accounts: IdentityUser[]
    }
}
export interface IdentityUser {
    value: string // This contains the AccountKey
}
export function getIdentityUserMe(userToken: string) {
    // API Reference https://confluence.ops.expertcity.com/spaces/SOA/pages/65145556/SCIM+Identity+Service#SCIMIdentityService-UserSchema
    return axios.get<GetIdentityUserMeResponseData>(`${process.env.AUTH_CLIENT_URL}/identity/v1/Users/me`, {
        headers: {
            Authorization: `Bearer ${userToken}`,
        },
    })
}

export interface FeatureFlagsResponseData {
    flags: string[]
}
const getFeatureFlags = memoize((siteType: SC_SITE): Promise<any> => {
    return axios.get<FeatureFlagsResponseData>('/ecommapi/content/featureflags', {
        params: { sc_site: siteType },
    })
})

export function validateReCAPTCHA(token: string) {
    return axios.post<ReCAPTCHAValidationResponseData>('/ecommapi/recaptcha/index', { token })
}

export default {
    getLocationInfo,
    clearUserSession,
    getCountries,
    getFlexTokenCall,
    canUserAuthenticate,
    getFeatureFlags,
}
